import { useQuery } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { FormControlLabel, Switch, Typography } from '@material-ui/core'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { BP_MODE } from '../constants'
import { useBestPracticeManagementStore } from '../store/BestPracticeManagementStore'
import { useBestPracticeManagementPanelStyles } from '../styles/useBestPracticeManagementPanelStyles'
import {
    BestPracticeTip,
    BPStagedConfig,
    emptyParameterSelected,
    emptyParameterToggles,
    initialBPStagedConfig,
    parameterConfig,
    ParameterKey,
    ParameterSelectedState,
    ParameterToggleState,
} from '../types/bpManagement.types'
import {
    buildFirmBPParameters,
    FirmDataShape,
    mapTipToParameterState,
    tipToStagedConfig,
} from '../utils/firmBPParameterUtils'
import { BestPracticeTipsList } from './BestPracticeTipsList'
import { ParameterListSection } from './ParameterListSection'

const buildEffectiveSelected = (
    parameterToggles: ParameterToggleState,
    parameterSelected: ParameterSelectedState
): ParameterSelectedState =>
    parameterConfig.reduce<ParameterSelectedState>(
        (acc, { key }) => {
            acc[key] = parameterToggles[key] ? parameterSelected[key] : []
            return acc
        },
        { ...emptyParameterSelected }
    )

/**
 * Computes the BPStagedConfig from current panel state synchronously.
 * Called in every handler so the store is updated in the same tick as the
 * local useState calls — no useEffect bridge needed.
 */
const buildStagedConfig = (
    isGlobal: boolean,
    isProjectParam: boolean,
    toggles: ParameterToggleState,
    selected: ParameterSelectedState
): BPStagedConfig => {
    if (isGlobal) {
        return {
            mode: BP_MODE.GLOBAL,
            parameterSelected: { ...emptyParameterSelected },
        }
    }
    const effectiveSelected = buildEffectiveSelected(toggles, selected)
    const hasAny = Object.values(effectiveSelected).some((v) => v.length > 0)
    if (!isProjectParam || !hasAny) {
        return {
            mode: BP_MODE.NONE,
            parameterSelected: { ...emptyParameterSelected },
        }
    }
    const projectGroup =
        toggles.projectGroup && selected.projectGroup.length > 0
            ? selected.projectGroup[0]
            : undefined
    const city =
        toggles.city && selected.city.length > 0 ? selected.city[0] : undefined
    const provinceState =
        toggles.provinceState && selected.provinceState.length > 0
            ? selected.provinceState[0]
            : undefined
    return {
        mode: BP_MODE.PARAMETER,
        projectGroup,
        city,
        provinceState,
        parameterSelected: effectiveSelected,
    }
}

export const BestPracticeManagementPanelContent = () => {
    const classes = useBestPracticeManagementPanelStyles()
    const { currentUserActiveFirm } = useContext(AppBarContext)
    const firmId = currentUserActiveFirm?.firmId
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const showAuditTips =
        isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace

    const [isProjectParameterModeOn, setIsProjectParameterModeOn] = useState(
        false
    )
    const [isGlobalBPModeOn, setIsGlobalBPModeOn] = useState(false)
    const [parameterToggles, setParameterToggles] = useState<
        ParameterToggleState
    >(emptyParameterToggles)
    const [parameterSelected, setParameterSelected] = useState<
        ParameterSelectedState
    >(emptyParameterSelected)

    const setOriginalTipStagedConfig = useBestPracticeManagementStore(
        (s) => s.setOriginalTipStagedConfig
    )
    const setStagedConfig = useBestPracticeManagementStore(
        (s) => s.setStagedConfig
    )

    const resetParameterPanel = useCallback(() => {
        setIsGlobalBPModeOn(false)
        setIsProjectParameterModeOn(false)
        setParameterToggles(emptyParameterToggles)
        setParameterSelected(emptyParameterSelected)
        setStagedConfig(initialBPStagedConfig)
    }, [setStagedConfig])

    const handleTipSelect = useCallback(
        (tip: BestPracticeTip | null) => {
            if (!tip) {
                setOriginalTipStagedConfig(null)
                resetParameterPanel()
                return
            }
            const stagedCfg = tipToStagedConfig(tip)
            setOriginalTipStagedConfig(stagedCfg)
            setStagedConfig(stagedCfg)
            const mapped = mapTipToParameterState(tip)
            setIsGlobalBPModeOn(mapped.isGlobal)
            setIsProjectParameterModeOn(
                !mapped.isGlobal &&
                    Object.values(mapped.parameterToggles).some(Boolean)
            )
            setParameterToggles(mapped.parameterToggles)
            setParameterSelected(mapped.parameterSelected)
        },
        [resetParameterPanel, setOriginalTipStagedConfig, setStagedConfig]
    )

    const { data: firmData } = useQuery<FirmDataShape>(
        FirmProjectParametersQuery,
        {
            variables: { firmId: firmId || '' },
            skip: !firmId,
        }
    )

    const firmOptions = useMemo(() => buildFirmBPParameters(firmData).options, [
        firmData,
    ])

    // Gated by parameterToggles, not just parameterSelected: turning off an
    // individual parameter's switch leaves its stale selected value(s) in
    // state (so the Select can re-show them if toggled back on), but that
    // value must not count toward "is anything actually selected".
    const effectiveParameterSelected = useMemo(
        () => buildEffectiveSelected(parameterToggles, parameterSelected),
        [parameterToggles, parameterSelected]
    )

    const hasSelectedParameter = Object.values(effectiveParameterSelected).some(
        (values) => values.length > 0
    )

    // Derived rather than synced via effect: once the last selected
    // parameter value is cleared, the toggle must read as off (not just
    // disabled) even though isProjectParameterModeOn itself hasn't reset.
    const isProjectParameterModeEffectivelyOn =
        isProjectParameterModeOn && hasSelectedParameter

    const handleProjectParameterToggle = (checked: boolean) => {
        setIsProjectParameterModeOn(checked)
        if (checked) {
            setIsGlobalBPModeOn(false)
            const newToggles = { ...parameterToggles }
            ;(Object.keys(newToggles) as ParameterKey[]).forEach((key) => {
                if (parameterSelected[key].length > 0) newToggles[key] = true
            })
            setParameterToggles(newToggles)
            setStagedConfig(
                buildStagedConfig(false, true, newToggles, parameterSelected)
            )
        } else {
            setParameterToggles(emptyParameterToggles)
            setStagedConfig(
                buildStagedConfig(
                    false,
                    false,
                    emptyParameterToggles,
                    parameterSelected
                )
            )
        }
    }

    const handleGlobalBPToggle = (checked: boolean) => {
        setIsGlobalBPModeOn(checked)
        if (checked) {
            setIsProjectParameterModeOn(false)
            setParameterToggles(emptyParameterToggles)
            setStagedConfig(
                buildStagedConfig(
                    true,
                    false,
                    emptyParameterToggles,
                    parameterSelected
                )
            )
        } else {
            setStagedConfig(
                buildStagedConfig(
                    false,
                    isProjectParameterModeOn,
                    parameterToggles,
                    parameterSelected
                )
            )
        }
    }

    const handleParameterToggle = (key: ParameterKey) => {
        const newChecked = !parameterToggles[key]
        let newSelected = parameterSelected
        let newIsProjectParam = isProjectParameterModeOn
        // Turning ON a parameter toggle exits global mode; turning OFF leaves it unchanged.
        const newIsGlobal = newChecked ? false : isGlobalBPModeOn

        if (newChecked) {
            newIsProjectParam = true
            // Default to first option so the user isn't forced into an explicit "Select..." step.
            if (parameterSelected[key].length === 0) {
                const firstOption = firmOptions[key]?.[0]
                if (firstOption) {
                    newSelected = { ...parameterSelected, [key]: [firstOption] }
                }
            }
        }

        const newToggles = { ...parameterToggles, [key]: newChecked }
        setIsGlobalBPModeOn(newIsGlobal)
        setIsProjectParameterModeOn(newIsProjectParam)
        setParameterToggles(newToggles)
        setParameterSelected(newSelected)
        setStagedConfig(
            buildStagedConfig(
                newIsGlobal,
                newIsProjectParam,
                newToggles,
                newSelected
            )
        )
    }

    const handleParameterSelectedChange = (
        key: ParameterKey,
        values: string[]
    ) => {
        const newSelected = { ...parameterSelected, [key]: values }
        // When all values for a key are cleared, flip its toggle off too.
        const newToggles =
            values.length === 0
                ? { ...parameterToggles, [key]: false }
                : parameterToggles
        setParameterSelected(newSelected)
        if (values.length === 0) {
            setParameterToggles(newToggles)
        }
        setStagedConfig(
            buildStagedConfig(
                isGlobalBPModeOn,
                isProjectParameterModeOn,
                newToggles,
                newSelected
            )
        )
    }

    return (
        <div className={classes.contentRoot}>
            {showAuditTips && (
                <>
                    <div className={classes.auditTipsScrollContainer}>
                        <BestPracticeTipsList onTipSelect={handleTipSelect} />
                    </div>
                    <div className={classes.divider} />
                </>
            )}

            <Typography className={classes.headerText}>
                You can apply the selected Best Practice parameters to any
                content by <b>double-clicking</b> it on the left.
            </Typography>

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isProjectParameterModeEffectivelyOn}
                control={<Switch />}
                disabled={!hasSelectedParameter}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Project Parameter Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) =>
                    handleProjectParameterToggle(checked)
                }
            />

            <div className={classes.divider} />

            <ParameterListSection
                firmOptions={firmOptions}
                parameterToggles={parameterToggles}
                parameterSelected={parameterSelected}
                onToggle={handleParameterToggle}
                onSelectedChange={handleParameterSelectedChange}
            />

            <div className={classes.divider} />

            <FormControlLabel
                className={classes.parameterRow}
                classes={{ root: classes.rowMarginLeftZero }}
                checked={isGlobalBPModeOn}
                control={<Switch color="primary" size="small" />}
                label={
                    <span className={classes.sectionTitle}>
                        Set as Global Best Practice
                    </span>
                }
                labelPlacement="start"
                onChange={(_e, checked) => handleGlobalBPToggle(checked)}
            />
        </div>
    )
}
